import createSelector from 'selectorator'
import { identity } from 'ramda'
import { RootState } from 'src/state/types'
import { NAMESPACE, userRoles } from './constants'
import { TTeamMember, TUserDetailsEntity, TUserState } from './types'

/**
 * Get a slice of the user API state
 * EG: getSlice('team')
 */
const getSlice = <K extends keyof TUserState>(key: K) =>
  createSelector<RootState, TUserState[K]>([`api.${NAMESPACE}.${key}`], identity)

export const isAuthenticated = getSlice('isAuthenticated')

export const getUserId = getSlice('userId')

/** Agencies this user can switch into — read by the agency switcher and the sidebar. */
export const getAgencies = getSlice('agencies')

export const hasSessionTimedOut = getSlice('hasSessionTimedOut')

export const getUserEmail = getSlice('email')

export const getUserFirstName = getSlice('firstName')

export const getUserLastName = getSlice('lastName')

export const emailVerified = getSlice('emailVerified')

export const cellVerified = getSlice('cellVerified')

// TODO: this is blank in state, not sure if its safe to use
export const getCurrentAgencyId = getSlice('currentAgencyId')

export const getUserRole = getSlice('currentAgencyRole')

export const isOwnerRole = createSelector<RootState, boolean>(
  [getUserRole],
  (role: TUserState['currentAgencyRole']): boolean => role === userRoles.Owner,
)

export const isTeamMemberRole = createSelector<RootState, boolean>(
  [getUserRole],
  (role: TUserState['currentAgencyRole']): boolean => role === userRoles.TeamMember,
)

export const isReadOnlyRole = createSelector<RootState, boolean>(
  [getUserRole],
  (role: TUserState['currentAgencyRole']): boolean => role === userRoles.ReadOnly,
)

export const getMisc = getSlice('misc')

export const getCellNumber = getSlice('cellNumber')

export const getOnboardingProgress = getSlice('onboardingProgress')

export const isValidSupportStaff = getSlice('isValidSupportStaff')

export const getOtpErrors = getSlice('otpErrors')

export const getAuthToken = getSlice('authToken')

export const getRefreshToken = getSlice('refreshToken')

export const users = getSlice('users')

export const getUserById = createSelector<RootState, (id?: string | null) => TUserDetailsEntity | undefined>(
  [users],
  (users: TUserDetailsEntity[]) =>
    (id?: string | null): TUserDetailsEntity | undefined =>
      users.find(user => user.id === id),
)

export const getTeam = getSlice('team')

export const getTeamMemberByUserId = createSelector<RootState, (userId?: string | null) => TTeamMember | undefined>(
  [getTeam],
  (team: TTeamMember[]) =>
    (userId?: string | null): TTeamMember | undefined =>
      team.find(member => member.userId === userId),
)

export const getCurrentSegments = getSlice('currentSegments')

/**
 * The default segment's id is the agency id, and it is not a real segment as far
 * as the segment drawer is concerned.
 */
export const getCurrentSegmentsWithoutDefault = createSelector<RootState, string[]>(
  [getCurrentSegments, getCurrentAgencyId],
  (segments: string[], defaultSegmentId: string | null): string[] => segments.filter(id => id !== defaultSegmentId),
)

export const getSegmentMemberships = getSlice('segmentMemberships')

export const getSegmentMembershipsWithoutDefault = createSelector<RootState, string[]>(
  [getSegmentMemberships, getCurrentAgencyId],
  (segments: string[], defaultSegmentId: string | null): string[] => segments.filter(id => id !== defaultSegmentId),
)

/** Support agency-access admin: members of one agency, keyed by agencyId. */
export const getAdminAgencyMembers = getSlice('adminAgencyMembers')

/** Support agency-access admin: the last email looked up. */
export const getAdminUserLookup = getSlice('adminUserLookup')

/** Support agency-access admin: suggestions for the email being typed. */
export const getAdminEmailMatches = getSlice('adminEmailMatches')

/** Support agency-access admin: per-agency outcomes of the last bulk add. */
export const getAdminGrantResults = getSlice('adminGrantResults')
